import CloseIcon from "../../assets/img/closeIcon.svg";
import useTranslator from "../../hooks/useTranslator";
import {
  FiAlertTriangle,
  FiBriefcase,
  FiCheckCircle,
  FiClipboard,
  FiDollarSign,
  FiFileText,
  FiLoader,
  FiRefreshCw,
  FiShield,
  FiTrendingUp,
  FiXCircle,
} from "react-icons/fi";
import "./../../styles/risk-assessment-report-modal.css";
import "./../../styles/linked-report-overview.css";
import LandLordContext from "../../context/landlord/LandLordContext";
import { Tooltip as ReactTooltip } from "react-tooltip";
import {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import PolicyMetricSection from "./PolicyMetricSection";
import AggregationStrategySelector from "./AggregationStrategySelector";
import DecisionCard from "./DecisionCard";
import { Modal } from "react-bootstrap";
import { useParams } from "react-router-dom";
import {
  haveValue,
  toast,
  getOnfidoResolutionStatus,
  normalizeResolutions,
} from "../../helper/common";
import { POLICY_SUB_SCORE_MAP } from "../../constants/policyConstants";
import VerdictPanel from "./VerdictPanel";
import {
  buildHardFailWarnings,
  buildBlockingRows,
} from "../../helper/verdictHelpers";
import {
  getPillarBadgeFromScore,
  PILLAR_NO_DATA_KEY,
  PILLAR_TITLE_KEYS,
  getPillarStatusFromScore,
} from "../../helper/pillarStatus";
import { getInsuranceReportStatus } from "../../Utils/insuranceReportStatus";

const getApplicantKey = (applicant, index = 0) =>
  applicant?._id ??
  `${applicant?.role ?? "applicant"}-${applicant?.name ?? "unknown"}-${index}`;

const RiskAssessmentReport = (props) => {
  const { _t } = useTranslator();
  const { workspaceId: routeWorkspaceId } = useParams();
  const landLordContext = useContext(LandLordContext);
  const {
    onClose,
    reportData,
    selectedTenants = [],
    setEnabled,
    enabled,
    finalEffortRate,
    areAllSelectedTenantsInsurable,
    handleGenerateLinkedReport,
    totalCombinedIncome,
    isInsurableEnabledLandlord,
    runInsuranceSimulationHandler,
    simulationLoading,
    isInsuranceSimulationSuccess,
    isInsuranceNotInsurable,
    insuranceErrorCode,
    insuranceErrorCodes = [],
    simulationDidNotRun,
    simulationPropertyErrors = [],
    simulationConsentErrors = [],
    onEditProperty,
    preFilledInsuranceDocument,
    isPropertyAwarded,
    isAwardSnapshotCompleted,
    isParentLoading = false,
    aggregationStrategy = "MIN",
    onAggregationStrategyChange,
    strategyChangeVersion = 0,
    isEditMode = false,
  } = props;
  const {
    getRiskAssessmentReport,
    markProfileResolved,
    getProfileResolutionStatus,
    unmarkProfileResolved,
  } = landLordContext;

  const [reportLoading, setReportLoading] = useState("loading");
  const [report, setReport] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPending, setIsPending] = useState(false);

  const [currentLang, setCurrentLang] = useState(
    localStorage.getItem("i18nextLng").toLowerCase() || "en-us"
  );
  const [selectedResolveTenantIds, setSelectedResolveTenantIds] = useState([]);
  const [resolvingDocs, setResolvingDocs] = useState(false);
  const [showResolveNoteModal, setShowResolveNoteModal] = useState(false);
  const [resolveNote, setResolveNote] = useState("");
  const [dataRefreshKey, setDataRefreshKey] = useState(0);
  const [individualResolutions, setIndividualResolutions] = useState({});
  const [individualWarnings, setIndividualWarnings] = useState({});
  const previousStrategyRef = useRef(aggregationStrategy);
  const previousStrategyChangeVersionRef = useRef(strategyChangeVersion);
  const reportRequestIdRef = useRef(0);

  useEffect(
    () => () => {
      reportRequestIdRef.current += 1;
    },
    []
  );
  const [kycWarnings, setKycWarnings] = useState({});

  useEffect(() => {
    setCurrentLang(localStorage.getItem("i18nextLng").toLowerCase() || "en-us");
  }, [localStorage.getItem("i18nextLng")]);

  const reportDataCleaned = reportData?.report ? reportData : null;

  console.log("reportDataCleaned", reportDataCleaned);
  console.log("report", report);

  const loadReportData = () => {
    if (!reportDataCleaned) return;
    const requestId = reportRequestIdRef.current + 1;
    reportRequestIdRef.current = requestId;
    setReportLoading("loading");
    getRiskAssessmentReport(reportDataCleaned)
      .then((res) => {
        if (requestId !== reportRequestIdRef.current) return;
        if (res?.data?.report) {
          setReport(res.data.report);
          setReportLoading("loaded");
        } else {
          setReportLoading("failed");
        }
      })
      .catch((error) => {
        if (requestId !== reportRequestIdRef.current) return;
        console.log(error);
        setReportLoading("failed");
      });
  };

  const langChecker = currentLang === "en-us" ? "en" : "es";

  useEffect(() => {
    const localStrategyChange =
      previousStrategyChangeVersionRef.current !== strategyChangeVersion;
    previousStrategyChangeVersionRef.current = strategyChangeVersion;
    const strategyChanged = previousStrategyRef.current !== aggregationStrategy;
    previousStrategyRef.current = aggregationStrategy;
    if (localStrategyChange || strategyChanged) {
      // The aggregation changes the decision, so refresh the enhanced report.
      // Insurance eligibility is independent of aggregation and does not need
      // to be simulated again.
      loadReportData();
      return;
    }
    loadReportData();
    if (isInsurableEnabledLandlord && !isEditMode) {
      const usedFor = "risk-assessment";
      runInsuranceSimulationHandler(usedFor);
    }
  }, [reportData, aggregationStrategy, strategyChangeVersion, isEditMode]);

  // Candidates include both tenants and guarantors. Guarantors are scored by
  // computeFinalResult and should be available in the same policy tabs.
  const applicants = useMemo(
    () => report?.applicants ?? reportData?.report?.applicants ?? [],
    [report, reportData]
  );

  const applicantKeys = useMemo(
    () =>
      applicants.map((applicant, index) => getApplicantKey(applicant, index)),
    [applicants]
  );
  const [activeApplicantKey, setActiveApplicantKey] = useState(
    () => applicantKeys[0] ?? ""
  );
  const activeTabId = Math.max(applicantKeys.indexOf(activeApplicantKey), 0);

  useEffect(() => {
    if (!applicantKeys.includes(activeApplicantKey)) {
      setActiveApplicantKey(applicantKeys[0] ?? "");
    }
  }, [applicantKeys, activeApplicantKey]);

  const decisionRaw =
    reportData?.report?.case?.decision ??
    report?.decision[langChecker] ??
    "Conditional";
  const decisionRawEnglish =
    reportData?.report?.case?.decision ??
    report?.decision["en"] ??
    "Conditional";

  const statusMeta = {
    Approved: { label: "Approved", className: "approved", Icon: FiCheckCircle },
    Conditional: {
      label: "Conditional",
      className: "conditional",
      Icon: FiAlertTriangle,
    },
    Rejected: { label: "Rejected", className: "rejected", Icon: FiXCircle },
  };
  const effectiveDecision = enabled ? "Approved" : decisionRawEnglish;
  const activeStatus = statusMeta[effectiveDecision] ?? statusMeta.Conditional;

  const isConditionalDecision = activeStatus.label === "Conditional";
  const showInsuranceApprovedOverride =
    enabled && decisionRawEnglish !== "Approved";

  const policyIconMap = {
    Affordability: <FiDollarSign size={15} />,
    "Employment Stability": <FiBriefcase size={15} />,
    "Financial behaviour": <FiTrendingUp size={15} />,
    "Financial Behaviour": <FiTrendingUp size={15} />,
    "Financial Behavior": <FiTrendingUp size={15} />,
    "Default Risk": <FiShield size={15} />,
    "Document Strength": <FiFileText size={15} />,
    "Credit History": <FiShield size={15} />,
    Documentation: <FiFileText size={15} />,
  };

  const formatSubScoreValue = (subScore) => {
    const noDataLabel = _t(PILLAR_NO_DATA_KEY);

    if (!subScore) return noDataLabel;

    const label = subScore?.label;
    if (label) return label;

    return noDataLabel;
  };

  const getDisplaySubScoreValue = (applicant, subScoreItem) => {
    if (
      subScoreItem.key === "effortRate" &&
      applicant?.tenantData?.displayEffortRate !== undefined &&
      applicant?.tenantData?.displayEffortRate !== null
    ) {
      return `${applicant.tenantData.displayEffortRate}%`;
    }

    return formatSubScoreValue(applicant?.subScores?.[subScoreItem.key]);
  };

  // Transform policy data into metric sections format
  const getMetricSections = () => {
    const activeApplicant = applicants[activeTabId];
    if (!activeApplicant) return [];
    if (!activeApplicant?.pillars) return [];
    console.log("APPLICANT ACTIVE", activeApplicant);

    const pillarConfig = [
      {
        key: "incomeAffordability",
        title: _t(PILLAR_TITLE_KEYS.incomeAffordability),
        requirementKey: "affordability",
      },
      {
        key: "employmentStability",
        title: _t(PILLAR_TITLE_KEYS.employmentStability),
        requirementKey: "employment stability",
      },
      {
        key: "financialBehaviour",
        title: _t(PILLAR_TITLE_KEYS.financialBehaviour),
        requirementKey: "financial behaviour",
      },
      {
        key: "creditDefaultRisk",
        title: _t(PILLAR_TITLE_KEYS.creditDefaultRisk),
        requirementKey: "default risk",
      },
      {
        key: "documentationStrength",
        title: _t(PILLAR_TITLE_KEYS.documentationStrength),
        requirementKey: "documentation",
      },
    ];

    return pillarConfig.map(({ key, title, requirementKey }) => {
      const score = activeApplicant?.pillars?.[key];
      const badge = getPillarBadgeFromScore(key, score);
      const subScoreItems = POLICY_SUB_SCORE_MAP[requirementKey] ?? [];
      const iconKeyByPillar = {
        incomeAffordability: "Affordability",
        employmentStability: "Employment Stability",
        financialBehaviour: "Financial Behaviour",
        creditDefaultRisk: "Default Risk",
        documentationStrength: "Documentation",
      };
      const icon = policyIconMap[iconKeyByPillar[key]] ?? (
        <FiFileText size={15} />
      );

      const rows = subScoreItems.map((subScoreItem) => {
        const subScore = activeApplicant?.subScores?.[subScoreItem.key];
        const hasDisplayEffortRate =
          subScoreItem.key === "effortRate" &&
          activeApplicant?.tenantData?.displayEffortRate !== undefined &&
          activeApplicant?.tenantData?.displayEffortRate !== null;
        return {
          label: subScoreItem.label[langChecker],
          value: getDisplaySubScoreValue(activeApplicant, subScoreItem),
          muted: !hasDisplayEffortRate && !subScore?.label,
        };
      });

      return {
        title,
        icon,
        badge: { label: _t(badge.labelKey), variant: badge.variant },
        rows,
      };
    });
  };

  const metricSections = getMetricSections();

  const getScoreToneClass = (score) => {
    const numericScore = Number(score);
    if (Number.isNaN(numericScore)) return "partial";
    if (numericScore >= 75) return "yes";
    if (numericScore >= 50) return "partial";
    return "no";
  };

  const getTone = (result = "") => {
    const lower = result?.en?.toLowerCase();
    if (lower === "strong" || lower === "acceptable") return "strong";
    if (lower === "weak") return "weak";
    return "medium";
  };

  // const handleCopy = () => {
  //     copy(JSON.stringify(reportData));
  //     alert(_t("copied"));
  // };

  const formatDocName = (doc) => {
    if (typeof doc === "string") return doc;
    return (
      doc?.localDocumentName?.[currentLang] ||
      doc?.documentNameTranslated?.[currentLang] ||
      doc?.localDocumentName?.[langChecker] ||
      doc?.documentNameTranslated?.[langChecker] ||
      doc?.documentName ||
      doc?.name ||
      doc?.label ||
      ""
    );
  };

  const getTenantActionReason = (applicant) => {
    const incomeAffordability = Number(
      applicant?.pillars?.incomeAffordability ?? 100
    );
    const applicantFinalScore = Number(applicant?.finalScore ?? 0);
    const effortRateValue = Number(finalEffortRate ?? 0);

    if (incomeAffordability < 60) {
      return _t("incomeDoesNotMeetMinimumRatioForThisRent");
    }

    if (effortRateValue > 35) {
      return _t("effortRateAboveAllowedThresholdForThisRent");
    }

    if (applicantFinalScore >= 65 && applicantFinalScore < 67) {
      return _t("scoreCloseToAutomaticApprovalThreshold");
    }

    return "";
  };

  const recommendedActionGroups = useMemo(() => {
    const candidateApplicants = report?.applicants ?? applicants;
    const candidateItems = candidateApplicants
      .map((applicant) => ({
        id: applicant?._id,
        candidateName: applicant?.name,
        role: applicant?.role,
        reason: getTenantActionReason(applicant),
      }))
      .filter((item) => item.candidateName && item.reason);

    const missingDocumentItems = candidateApplicants
      .map((applicant) => {
        const docs =
          applicant?.subScores?.documentation?.missingDocs
            ?.map(formatDocName)
            ?.filter(Boolean) ?? [];

        return {
          candidateName: applicant?.name,
          role: applicant?.role,
          docs,
        };
      })
      .filter((item) => item.candidateName && item.docs.length > 0);

    return {
      candidateItems,
      missingDocumentItems,
    };
  }, [report, applicants, finalEffortRate, langChecker, currentLang, _t]);
  const hasRecommendedActionItems =
    recommendedActionGroups.candidateItems.length > 0 ||
    recommendedActionGroups.missingDocumentItems.length > 0;

  // ── Resolve missing documents + hard fails (merge api + local to keep vals/errorEntries) ──
  // Driven by localApps (current selection) to avoid leaking previous profile's blocking checks
  const unifiedApplicants = useMemo(() => {
    const apiApps = report?.applicants || [];
    const localApps = reportData?.report?.applicants || [];
    if (!localApps.length) return apiApps;
    if (!apiApps.length) return localApps;
    const apiById = new Map(apiApps.map((a) => [a._id, a]));
    return localApps.map((local) => {
      const api = apiById.get(local._id);
      if (!api) return local;
      return {
        ...local,
        ...api,
        vals: { ...(local.vals || {}), ...(api.vals || {}) },
        errorEntries: api.errorEntries?.length
          ? api.errorEntries
          : local.errorEntries || [],
        isHardFail: api.isHardFail ?? local.isHardFail,
        tenantData: { ...local.tenantData, ...(api.tenantData || {}) },
      };
    });
  }, [report, reportData]);

  const hardFailWarnings = useMemo(
    () => buildHardFailWarnings(unifiedApplicants),
    [unifiedApplicants]
  );

  // ── KYC raw tenant maps from selectedTenants (tenant only) ──
  const tenantRawById = useMemo(() => {
    const map = {};
    const tenants = Array.isArray(selectedTenants) ? selectedTenants : [];
    tenants.forEach((sp) => {
      // sp is a sharedProfile; primary tenant in sp.tenantid
      const primary = sp?.tenantid;
      if (primary?._id) map[primary._id] = primary;
      if (primary?.id) map[primary.id] = primary;
      // application-type: participants
      (sp?.application_id?.participants || []).forEach((p) => {
        const ti = p?.tenant_info;
        if (ti?._id) map[ti._id] = ti;
        if (ti?.id) map[ti.id] = ti;
      });
    });
    return map;
  }, [selectedTenants]);

  const tenantSharedProfileIdById = useMemo(() => {
    const map = {};
    const tenants = Array.isArray(selectedTenants) ? selectedTenants : [];
    tenants.forEach((sp) => {
      const spId = sp?._id;
      if (!spId) return;
      const primary = sp?.tenantid;
      if (primary?._id) map[primary._id] = spId;
      if (primary?.id) map[primary.id] = spId;
      (sp?.application_id?.participants || []).forEach((p) => {
        const ti = p?.tenant_info;
        if (ti?._id) map[ti._id] = spId;
        if (ti?.id) map[ti.id] = spId;
      });
    });
    return map;
  }, [selectedTenants]);

  const getProfileIsResolved = useCallback(
    async (profileId) => {
      if (!haveValue(profileId)) return null;
      try {
        const res = await getProfileResolutionStatus(profileId, "documents");
        return res?.isResolved ? res : null;
      } catch (err) {
        console.log("Failed to fetch profile resolution status", err);
        return null;
      }
    },
    [getProfileResolutionStatus]
  );

  useEffect(() => {
    if (!unifiedApplicants?.length) {
      setIndividualWarnings({});
      setIndividualResolutions({});
      return;
    }
    let cancelled = false;
    const build = async () => {
      const warnings = {};
      const resolutions = {};
      await Promise.all(
        unifiedApplicants.map(async (applicant) => {
          const rawMissing =
            applicant?.subScores?.documentation?.missingDocs ??
            applicant?.vals?.MISSING_DOCS ??
            [];
          const missingDocs = rawMissing.map(formatDocName).filter(Boolean);
          if (!missingDocs.length) return;
          const resolution = await getProfileIsResolved(applicant._id);
          if (cancelled) return;
          if (resolution) resolutions[applicant._id] = resolution;
          warnings[applicant._id] = {
            label: applicant.name || applicant.label || applicant._id,
            docsMissing: missingDocs,
            isResolved: !!resolution,
          };
        })
      );
      if (!cancelled) {
        setIndividualWarnings(warnings);
        setIndividualResolutions(resolutions);
      }
    };
    build();
    return () => {
      cancelled = true;
    };
  }, [unifiedApplicants, dataRefreshKey, getProfileIsResolved]);

  // ── KYC unclear categories (tenant only, no checkbox — click to open profile) ──
  useEffect(() => {
    const tenantsOnly = unifiedApplicants.filter(
      (a) => a?.role !== "guarantor"
    );
    if (!tenantsOnly.length) {
      setKycWarnings({});
      return;
    }
    let cancelled = false;
    const build = async () => {
      const warnings = {};
      try {
        await Promise.all(
          tenantsOnly.map(async (applicant) => {
            const tenantId = applicant?._id;
            if (!tenantId) return;
            const raw =
              tenantRawById[tenantId] ||
              tenantRawById[tenantId?.toString?.()] ||
              null;
            // fallback to applicant if raw not found but still try to get output from raw
            const userObj = raw || applicant;
            const output =
              userObj?.onfido_user_kyc?.onfido_workflow_res?.output ??
              userObj?.onfido_user_kyc?.onfido_workflow_res?.resource?.output ??
              null;
            if (!output) return;
            // check if output has any breakdown at all
            const hasBreakdown =
              output?.document_report?.breakdown ||
              output?.aml_report?.breakdown;
            if (!hasBreakdown) return;
            let kycRes = null;
            try {
              kycRes = await getProfileResolutionStatus(tenantId, "kyc");
            } catch (e) {
              console.log("Failed to fetch KYC status for", tenantId, e);
              kycRes = null;
            }
            if (cancelled) return;
            const onfidoStatus = getOnfidoResolutionStatus(output, kycRes);
            const unresolved = onfidoStatus.categoryResults.nonClear.filter(
              (c) => !onfidoStatus.resolvedCategoryKeys.has(c.key)
            );
            if (unresolved.length === 0) return;
            warnings[tenantId] = {
              label: applicant.name || applicant.label || tenantId,
              unresolved,
              unresolvedCount: unresolved.length,
              tenantId,
            };
          })
        );
      } catch (e) {
        console.log("Failed to build KYC warnings", e);
      }
      if (!cancelled) setKycWarnings(warnings);
    };
    build();
    return () => {
      cancelled = true;
    };
  }, [
    unifiedApplicants,
    tenantRawById,
    dataRefreshKey,
    getProfileResolutionStatus,
  ]);

  const handleKycTenantClick = useCallback(
    (tenantId) => {
      const spId = tenantSharedProfileIdById[tenantId];
      if (!spId || !tenantId) return;
      // workspace from the route param first, then URL path, then storage
      const wsFromPath = window.location.pathname.match(
        /^\/([A-Za-z0-9-]+)(?:\/|$)/
      )?.[1];
      const ws =
        routeWorkspaceId ||
        wsFromPath ||
        localStorage.getItem("current_workspace_uuid") ||
        localStorage.getItem("current_workspace") ||
        "";
      const base = ws ? `/${ws}/profile-property` : "/profile-property";
      const url = `${base}?sharedprofileid=${spId}&tenant_id=${tenantId}&kycAml=true`;
      window.open(url, "_blank", "noopener,noreferrer");
    },
    [tenantSharedProfileIdById, routeWorkspaceId]
  );

  const toggleResolveTenant = (tenantId) => {
    if (!tenantId) return;
    setSelectedResolveTenantIds((prev) =>
      prev.includes(tenantId)
        ? prev.filter((id) => id !== tenantId)
        : [...prev, tenantId]
    );
  };

  const pendingResolveIds = useMemo(
    () =>
      selectedResolveTenantIds.filter(
        (id) => !individualWarnings?.[id]?.isResolved
      ),
    [selectedResolveTenantIds, individualWarnings]
  );
  const pendingUnresolveIds = useMemo(() => {
    const selectedSet = new Set(selectedResolveTenantIds);
    return Object.entries(individualWarnings || {})
      .filter(([, w]) => w?.isResolved)
      .map(([id]) => id)
      .filter((id) => !selectedSet.has(id));
  }, [selectedResolveTenantIds, individualWarnings]);

  const hasPendingChanges =
    pendingResolveIds.length > 0 || pendingUnresolveIds.length > 0;

  useEffect(() => {
    const resolvedIds = Object.entries(individualWarnings || {})
      .filter(([, w]) => w?.isResolved)
      .map(([id]) => id);
    setSelectedResolveTenantIds((prev) => {
      const prevPendingResolve = prev.filter(
        (id) => !individualWarnings?.[id]?.isResolved
      );
      return [...new Set([...resolvedIds, ...prevPendingResolve])];
    });
  }, [individualWarnings]);

  const resolveActionLabel = useMemo(() => {
    const hasResolve = pendingResolveIds.length > 0;
    const hasUnresolve = pendingUnresolveIds.length > 0;
    if (hasResolve && hasUnresolve) return _t("update", "Update");
    if (hasUnresolve) return _t("unresolve", "Unresolve");
    return _t("markResolved", "Mark resolved");
  }, [pendingResolveIds, pendingUnresolveIds, _t]);

  const handleApplyResolutions = async (note) => {
    if (!hasPendingChanges) return;
    setResolvingDocs(true);
    try {
      const ops = [];
      pendingResolveIds.forEach((tenantId) =>
        ops.push(
          markProfileResolved({ tenant_id: tenantId, type: "documents", note })
        )
      );
      pendingUnresolveIds.forEach((tenantId) =>
        ops.push(unmarkProfileResolved(tenantId, "documents"))
      );
      await Promise.all(ops);
      if (pendingResolveIds.length && pendingUnresolveIds.length)
        toast.success(_t("documentsUpdated", "Documents updated"));
      else if (pendingUnresolveIds.length)
        toast.success(_t("documentsUnresolved", "Documents unresolved"));
      else
        toast.success(
          _t("documentsMarkedResolved", "Documents marked resolved")
        );
      setResolveNote("");
      setShowResolveNoteModal(false);
      setDataRefreshKey((k) => k + 1);
    } catch (err) {
      console.log(err, "Error applying document resolutions");
      toast.error(_t("failedToUpdate", "Failed to update"));
    } finally {
      setResolvingDocs(false);
    }
  };

  const formatMissingDoc = (val) => {
    const payslipMatch = val.match(/^payslip\s*-\s*(.+)$/i);
    if (payslipMatch) return `${_t("Payslip")} - ${payslipMatch[1]}`;
    return _t(val);
  };

  const hasMissingDocsForResolve = Object.keys(individualWarnings).length > 0;
  const [expandedDocsRisk, setExpandedDocsRisk] = useState({});
  const toggleDocsExpandedRisk = (id) =>
    setExpandedDocsRisk((prev) => ({ ...prev, [id]: !prev[id] }));
  const blockingRowsRisk = useMemo(
    () =>
      buildBlockingRows(
        hardFailWarnings,
        _t,
        decisionRawEnglish === "Rejected" ||
          report?.decision?.en === "Rejected",
        report?.recommendation || reportData?.report?.case?.recommendation || []
      ),
    [hardFailWarnings, decisionRawEnglish, report, reportData, _t]
  );

  const insuranceAction = report?.recommendedActions?.find(
    (action) => action?.type === "insurance"
  );

  const handleInsuranceRecommendation = async () => {
    setIsPending(true);
    try {
      setEnabled((prev) => !prev);
    } catch (err) {
      console.log(err);
    } finally {
      setIsPending(false);
    }
  };

  const renderContent = () => {
    if (!reportDataCleaned) {
      return (
        <div className="risk-report-state-container">
          <div className="risk-report-state-icon risk-report-state-icon--error">
            <FiClipboard size={32} />
          </div>
          <p className="risk-report-state-title">{_t("noReportDataFound")}</p>
          <p className="risk-report-state-desc">
            {_t("noApplicationDataWasProvidedToGenerateARiskAssessmentReport")}
          </p>
        </div>
      );
    }

    if (reportLoading === "loading" || simulationLoading || isParentLoading) {
      return (
        <div className="risk-report-state-container">
          <div className="risk-report-state-icon risk-report-state-icon--spinning">
            <FiLoader color="#A3EB65" size={32} />
          </div>

          <p className="risk-report-state-title">
            {(reportLoading === "loading" || isParentLoading) &&
              _t("generatingReport")}

            {(reportLoading === "loading" || isParentLoading) &&
              simulationLoading && <> {_t("and")} </>}

            {simulationLoading && _t("analysingFeasibility")}
          </p>

          <p className="risk-report-state-desc">
            {_t("wereAnalyzingTheApplicationThisMayTakeAFewSeconds")}
          </p>
        </div>
      );
    }

    if (reportLoading === "failed") {
      return (
        <div className="risk-report-state-container">
          <div className="risk-report-state-icon risk-report-state-icon--error">
            <FiXCircle size={32} />
          </div>
          <p className="risk-report-state-title">{_t("failedToLoadReport")}</p>
          <p className="risk-report-state-desc">
            {_t(
              "somethingWentWrongWhileGeneratingTheRiskAssessmentPleaseTryAgain"
            )}
          </p>
          <button className="risk-report-retry-btn" onClick={loadReportData}>
            <FiRefreshCw size={14} />
            {_t("retry")}
          </button>
        </div>
      );
    }

    return (
      <div className="risk-assessment-report-content">
        {/* ── Decision Card ── */}

        <div className="risk-assessment-report-decision-card">
          <DecisionCard
            decisionTitle={
              showInsuranceApprovedOverride ? (
                <span className="risk-report-decision-transition">
                  <span className="risk-report-decision-original">
                    {decisionRaw}
                  </span>
                  <span className="risk-report-decision-approved">
                    <FiCheckCircle size={14} />
                    {_t("approved")}
                  </span>
                </span>
              ) : (
                _t(activeStatus.label)
              )
            }
            decisionStatus={enabled ? "Approved" : activeStatus.label}
            combinedIncome={totalCombinedIncome}
            combinedEffortRate={`${finalEffortRate}%`}
            decisionSummary={report?.decisionSummary}
            insurable={
              simulationDidNotRun
                ? null
                : areAllSelectedTenantsInsurable && isInsuranceSimulationSuccess
            }
            insurabilityStatus={getInsuranceReportStatus({
              isInsurable: isInsuranceSimulationSuccess,
              isNotInsurable: isInsuranceNotInsurable,
            })}
            insurabilityErrorCode={insuranceErrorCode}
            insurabilityErrorCodes={insuranceErrorCodes}
            preFilledInsuranceDocument={preFilledInsuranceDocument}
            currentLang={langChecker}
            insurabilityContactOnly={isEditMode}
          >
            <VerdictPanel
              _t={_t}
              individualWarnings={individualWarnings}
              individualResolutions={individualResolutions}
              selectedResolveTenantIds={selectedResolveTenantIds}
              toggleResolveTenant={toggleResolveTenant}
              formatMissingDoc={formatMissingDoc}
              expandedDocs={expandedDocsRisk}
              toggleDocsExpanded={toggleDocsExpandedRisk}
              hasPendingChanges={hasPendingChanges}
              resolvingDocs={resolvingDocs}
              resolveActionLabel={resolveActionLabel}
              onResolveClick={() => setShowResolveNoteModal(true)}
              blockingRows={blockingRowsRisk}
              guarantorItems={
                isConditionalDecision || enabled
                  ? recommendedActionGroups.guarantorItems
                  : []
              }
              kycWarnings={kycWarnings}
              onKycTenantClick={handleKycTenantClick}
            />
            {insuranceAction &&
              !isEditMode &&
              isInsurableEnabledLandlord &&
              isInsuranceSimulationSuccess && (
                <ul className="risk-report-action-list">
                  <li
                    className={`risk-report-action-item insurance-action ${enabled ? "insurance-action--enabled" : ""}`}
                  >
                    <div className="insurance-action-content">
                      <div className="insurance-action-left">
                        <FiShield
                          size={16}
                          className={`insurance-action-icon ${enabled ? "active" : ""}`}
                        />
                        <div className="insurance-action-copy">
                          <span className="insurance-action-title">
                            {_t(
                              enabled
                                ? "rentDefaultInsuranceIncluded"
                                : "insuranceUnlocksFullApproval"
                            )}
                          </span>
                          <span className="insurance-action-subtitle">
                            {_t(
                              enabled
                                ? "insuranceIncludedFullApprovalSubtitle"
                                : "insuranceUnlocksFullApprovalSubtitle"
                            )}
                          </span>
                        </div>
                      </div>
                      <label className="insurance-toggle">
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={handleInsuranceRecommendation}
                          disabled={isPending}
                          className="insurance-toggle-input"
                        />
                        <span className="insurance-toggle-slider"></span>
                        <span className="insurance-toggle-knob"></span>
                      </label>
                    </div>
                  </li>
                </ul>
              )}
            {simulationDidNotRun &&
              !isEditMode &&
              isInsurableEnabledLandlord && (
                <div className="risk-report-recommendation-group">
                  <div className="risk-report-recommendation-heading">
                    <span className="risk-report-recommendation-bullet" />
                    <strong>
                      {_t(
                        "notAbleToRunInsuranceSimulationBecauseOfMissingInformation",
                        "Not able to run insurance simulation because of missing information."
                      )}
                      {simulationPropertyErrors.length > 0 && (
                        <>
                          {" "}
                          {_t(
                            "updatePropertySettings",
                            "Update property settings"
                          )}{" "}
                          <a
                            href="#"
                            style={{
                              color: "inherit",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              onEditProperty?.();
                            }}
                          >
                            {_t("here", "here")}
                          </a>
                        </>
                      )}
                    </strong>
                  </div>
                  {[
                    ...simulationPropertyErrors,
                    ...simulationConsentErrors,
                  ].map((err, i) => (
                    <p className="risk-report-recommendation-item" key={i}>
                      {err}
                    </p>
                  ))}
                </div>
              )}
          </DecisionCard>
        </div>

        {/* Divider */}
        <div className="risk-report-divider" />

        {/* ── Policy Fit + Impact (side by side if both present) ── */}
        <div className="risk-report-grid">
          {/* Policy Fit Summary */}
          {metricSections.length > 0 && applicants.length > 0 && (
            <section className="risk-report-section policy-fit-section">
              <div className="risk-report-policy-heading">
                <p className="risk-report-section-title m-0">
                  {_t("policyFitSummary")}
                </p>
                <AggregationStrategySelector
                  value={aggregationStrategy}
                  onChange={onAggregationStrategyChange}
                />
              </div>
              <div className="d-flex align-items-center flex-wrap gap-8px tab-main-wrapper">
                {applicants?.map((applicant, index) => (
                  <span
                    key={applicantKeys[index]}
                    className={`user-tab ${activeTabId === index ? "active-tab" : ""}`}
                    onClick={() => setActiveApplicantKey(applicantKeys[index])}
                  >
                    {applicant.name}
                  </span>
                ))}
              </div>
              <div className="linked-report__metrics-grid risk-report-policy-grid">
                {metricSections.map((section, index) => (
                  <PolicyMetricSection
                    key={index}
                    title={section.title}
                    icon={section.icon}
                    badge={section.badge}
                    rows={section.rows}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Impact */}
          {enabled && report?.impact && (
            <section className="risk-report-section">
              <p className="risk-report-section-title">
                {_t("ifRecommendationIsCompleted")}
              </p>
              <div className="risk-report-outcome-card">
                <div className="risk-report-outcome-row">
                  <span>{_t("projectedOutcome")}</span>
                  <strong className="risk-report-approved-text">
                    <FiCheckCircle size={14} />
                    {_t("approved")}
                  </strong>
                </div>
                <p className="risk-report-outcome-note">
                  {report.impact[langChecker]?.split("\n").join(" ")}
                </p>
              </div>
            </section>
          )}
        </div>

        <p className="risk-report-legal-footer">
          {_t(
            "riskAssessmentLegalFooter",
            "This decision was generated using the landlord's predefined Skor policy. All applicants are assessed consistently using the same objective criteria. Guarantors and insurance are treated as risk mitigators and do not override affordability limits, default history, or identity checks."
          )}
        </p>
      </div>
    );
  };

  return (
    <div
      className="risk-assessment-report-modal metermodal modal-xl wrapper-modal modalWidth_set"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="noTopPadding doc-history-popup rental-overview-popup total-salary-modal share-profile-modal sharedTenantProfileModal open "
        onClick={(e) => e.stopPropagation()}
      >
        <div className="doc-history-header linked-property-popup-padding">
          <div className="d-flex align-items-center custom-gap-2">
            <img
              src={CloseIcon}
              alt=""
              className="doc-history-close-btn"
              onClick={onClose}
            />
            <h4 className="custom-modal-heading">
              {_t("riskAssessmentReport")}
            </h4>
          </div>
          {!(reportLoading === "loading" || simulationLoading) && (
            <button
              type="button"
              className="btnNext commonButtonPropertyDark risk-report-generate-btn"
              onClick={() =>
                handleGenerateLinkedReport({
                  localReport: reportDataCleaned,
                  apiReport: report,
                })
              }
              style={{ marginLeft: "auto", marginRight: "1.3889vw" }}
            >
              {isPropertyAwarded && isAwardSnapshotCompleted
                ? _t("viewReport", "Ver Informe(ES)/View report(EN)")
                : _t("generateReport")}
            </button>
          )}
        </div>
        <div className="risk-report-divider" style={{ marginTop: 0 }} />
        <div className="rental-overview-scroll">{renderContent()}</div>
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        <Modal
          show={showResolveNoteModal}
          onHide={() => setShowResolveNoteModal(false)}
          centered
          className="newmodalui max-w-lg tailwind-base-modal wrapper-modal resolve-documents-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <span>{resolveActionLabel}</span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="resolve-documents-modal__summary">
              {pendingResolveIds.length > 0 && pendingUnresolveIds.length > 0
                ? _t("updatingDocumentsFor", "Updating documents for")
                : pendingUnresolveIds.length > 0
                  ? _t("unresolvingDocumentsFor", "Unresolving documents for")
                  : _t("resolvingDocumentsFor", "Resolving documents for")}{" "}
              <strong>{selectedResolveTenantIds.length}</strong>{" "}
              {_t("tenantS", "tenant(s)")}
            </p>
            {pendingResolveIds.length > 0 && (
              <div className="resolve-documents-modal__field">
                <textarea
                  className="resolve-documents-modal__textarea"
                  placeholder={_t("addNote", "Add a note (optional)")}
                  aria-label={_t("addNote", "Add a note (optional)")}
                  maxLength={500}
                  value={resolveNote}
                  onChange={(e) => setResolveNote(e.target.value)}
                />
                <span className="resolve-documents-modal__count">
                  {resolveNote?.length}/500
                </span>
              </div>
            )}
            {pendingUnresolveIds.length > 0 &&
              pendingResolveIds.length === 0 && (
                <p className="resolve-documents-modal__hint">
                  {_t(
                    "unresolveHint",
                    "This will mark the documents as unresolved again."
                  )}
                </p>
              )}
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="resolve-documents-modal__button resolve-documents-modal__button--secondary"
              onClick={() => setShowResolveNoteModal(false)}
            >
              {_t("cancel")}
            </button>
            <button
              type="button"
              className="resolve-documents-modal__button resolve-documents-modal__button--primary"
              disabled={resolvingDocs}
              onClick={() => handleApplyResolutions(resolveNote)}
            >
              {resolvingDocs
                ? `${_t("updating", "Updating")}...`
                : resolveActionLabel}
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default RiskAssessmentReport;
